import type {
  AchievementDto,
  ActivityDto,
  CourseCategoryDto,
  CourseDto,
  DiseaseDto,
  DocLabelDto,
  DoctorDto,
  HospitalDto,
  PartnerDto,
  PortalSettingDto,
  VideoCategoryDto,
  VideoDto,
} from './types';
import type {
  AchievementItem,
  CourseItem,
  DiseaseItem,
  DoctorItem,
  HospitalItem,
  PartnerItem,
  PortalSettingView,
  TrainingEventItem,
  VideoItem,
} from '@/types/content';
import { resolveAssetUrl } from './request';

/**
 * 后端 DTO -> 官网视图模型 的字段映射。
 * 后端图片字段优先使用已拼接资源域名的全路径字段（ShowImgFull / ShowImgStr 等），
 * 统一经 resolveAssetUrl 处理（HTTPS 协议升级 / 相对路径拼接资源域名）。
 */

const safe = (value?: string | null): string => value || '';

/** 数字格式化：12345 -> "1.2万"；3600 -> "3600" */
export function formatCount(value?: number | null): string {
  const num = value ?? 0;
  if (num >= 10000) {
    const w = num / 10000;
    return `${w >= 10 ? Math.round(w) : Math.round(w * 10) / 10}万`;
  }
  return String(num);
}

/** 报名费格式化：1980 -> "¥1980"；1980.5 -> "¥1980.50"；0/空 -> "免费" */
export function formatSignFee(value?: number | null): string {
  const num = value ?? 0;
  if (num <= 0) return '免费';
  return `¥${Number.isInteger(num) ? num : num.toFixed(2)}`;
}

/** 课程 DTO -> 课程卡片 */
export function adaptCourse(dto: CourseDto): CourseItem {
  return {
    id: dto.id,
    title: safe(dto.name),
    category: safe(dto.cateName),
    doctor: safe(dto.doctorInfo?.name),
    lessons: dto.courseCount ?? 0,
    price: dto.shopPrice ?? 0,
    image: resolveAssetUrl(dto.showImgFull || dto.showImg),
    courseType: dto.courseType,
  };
}

export function adaptCourses(list: CourseDto[] | undefined | null): CourseItem[] {
  return (list ?? []).map(adaptCourse);
}

/** 课程详情（含主讲老师信息） */
export interface CourseDetailItem extends CourseItem {
  description: string;
  totalDuration: string;
  /** 课程亮点（后端配置；空数组时页面回退默认内容） */
  highlights: { icon: string; title: string; desc: string }[];
  /** 适合人群（后端配置；空数组时页面回退默认内容） */
  audiences: string[];
  teacher: {
    name: string;
    title: string;
    hospital: string;
    intro: string;
    avatar: string;
  } | null;
}

/** JSON 字符串安全解析（空值/解析失败返回 null） */
function parseJsonSafe<T>(raw?: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** 课程详情 DTO -> 详情视图数据（doctorInfo 为精简版医师，称号降级用等级字段） */
export function adaptCourseDetail(dto: CourseDto): CourseDetailItem {
  const teacher = dto.doctorInfo;
  const rawHighlights = parseJsonSafe<{ icon?: string; title?: string; desc?: string }[]>(
    dto.highlightsJson,
  );
  const rawAudiences = parseJsonSafe<string[]>(dto.audiencesJson);
  return {
    ...adaptCourse(dto),
    description: safe(dto.description),
    totalDuration: safe(dto.totalDuration),
    highlights: (Array.isArray(rawHighlights) ? rawHighlights : [])
      .filter((it) => it && it.title)
      .map((it) => ({
        icon: safe(it.icon) || 'ri-book-open-line',
        title: safe(it.title),
        desc: safe(it.desc),
      })),
    audiences: (Array.isArray(rawAudiences) ? rawAudiences : [])
      .map((item) => safe(item))
      .filter(Boolean),
    teacher:
      teacher && teacher.name
        ? {
            name: safe(teacher.name),
            title: safe(teacher.level),
            hospital: safe(teacher.hospital),
            intro: safe(teacher.description),
            avatar: resolveAssetUrl(teacher.showImgFull || teacher.showImg),
          }
        : null,
  };
}

/** "yyyy-MM-dd HH:mm" -> 拆分日期与时间 */
function splitDateTime(value?: string | null): { date: string; time: string } {
  const raw = safe(value);
  if (!raw) return { date: '', time: '' };
  const [datePart = '', timePart = ''] = raw.split(' ');
  return { date: datePart.replace(/-/g, '.'), time: timePart };
}

/** 活动 DTO -> 培训事件 */
export function adaptTrainingEvent(dto: ActivityDto): TrainingEventItem {
  const start = splitDateTime(dto.activityStartTimeStr || dto.activityStartTime);
  const end = splitDateTime(dto.activityEndTimeStr || dto.activityEndTime);
  const year = Number(start.date.slice(0, 4)) || new Date().getFullYear();

  return {
    id: dto.id,
    year,
    type: safe(dto.activityTypeStr) || '培训',
    topic: safe(dto.name),
    hospital: safe(dto.address) || safe(dto.regionModule?.alias) || safe(dto.addressRemark),
    date: start.date,
    time: end.time ? `${start.time} - ${end.time}` : start.time,
    enrolled: dto.userCount ?? 0,
    capacity: dto.maxParticipants ?? 0,
    signFee: dto.signFee ?? 0,
    speakers: (dto.activitySignDoctorList ?? [])
      .map((item) => safe(item.doctorInfo?.name))
      .filter(Boolean),
    image: resolveAssetUrl(dto.showImgStr || dto.showImg),
  };
}

export function adaptTrainingEvents(list: ActivityDto[] | undefined | null): TrainingEventItem[] {
  return (list ?? []).map(adaptTrainingEvent);
}

/** 视频 DTO -> 视频卡片（后端无视频时长字段，duration 降级为空） */
export function adaptVideo(dto: VideoDto): VideoItem {
  return {
    id: dto.id,
    title: safe(dto.name),
    category: safe(dto.cateName),
    doctor: safe(dto.doctorInfo?.name),
    views: formatCount(dto.browseCount),
    duration: '',
    image: resolveAssetUrl(dto.showImgStr || dto.showImg),
    videoUrl: resolveAssetUrl(dto.urlStr || dto.url),
  };
}

export function adaptVideos(list: VideoDto[] | undefined | null): VideoItem[] {
  return (list ?? []).map(adaptVideo);
}

/** 医师称号组合：级别 · 身份 · 个人标签 */
function buildDoctorTitle(dto: DoctorDto): string {
  const parts = [
    safe(dto.levelLabel?.name) || safe(dto.level),
    safe(dto.identityLabel?.name),
    ...(dto.personalLabelList ?? []).map((label) => safe(label.name)),
  ].filter(Boolean);
  return parts.join(' · ');
}

/** 医师 DTO -> 医师卡片 */
export function adaptDoctor(dto: DoctorDto): DoctorItem {
  const tags = (dto.subjectLabelList ?? []).map((label) => safe(label.name)).filter(Boolean);
  const fallbackTags = safe(dto.subjectListStr)
    .split(/[,，、;；|]/)
    .map((tag) => tag.trim())
    .filter(Boolean);

  return {
    id: dto.id,
    name: safe(dto.name),
    title: buildDoctorTitle(dto),
    hospital: safe(dto.hospital),
    tags: tags.length ? tags : fallbackTags,
    intro: safe(dto.description),
    avatar: resolveAssetUrl(dto.showImgFull || dto.showImg || dto.avatar),
  };
}

export function adaptDoctors(list: DoctorDto[] | undefined | null): DoctorItem[] {
  return (list ?? []).map(adaptDoctor);
}

/** 合作伙伴 DTO -> 合作伙伴（后端无简称字段，short 取名称） */
export function adaptPartner(dto: PartnerDto): PartnerItem {
  return {
    id: dto.id,
    name: safe(dto.name),
    short: safe(dto.name),
    logo: resolveAssetUrl(dto.showImgFull || dto.showImg),
  };
}

export function adaptPartners(list: PartnerDto[] | undefined | null): PartnerItem[] {
  return (list ?? []).map(adaptPartner);
}

/** 课程分类 DTO -> 分类名称 */
export function adaptCourseCategory(dto: CourseCategoryDto): { id: number; name: string } {
  return { id: dto.id, name: safe(dto.name) };
}

/** 视频分类 DTO -> 分类名称 */
export function adaptVideoCategory(dto: VideoCategoryDto): { id: number; name: string } {
  return { id: dto.id, name: safe(dto.name) };
}

/** 医师标签 DTO -> 标签信息 */
export function adaptDocLabel(dto: DocLabelDto): { id: number; name: string; labelType?: number } {
  return { id: dto.id, name: safe(dto.name), labelType: dto.labelType };
}

/** 医院 DTO -> 医院视图（列表与详情通用，详情额外含优势病种） */
export function adaptHospital(dto: HospitalDto): HospitalItem {
  return {
    id: dto.id,
    name: safe(dto.name),
    image: resolveAssetUrl(dto.showImgFull || dto.showImg),
    intro: safe(dto.intro),
    website: safe(dto.website),
    campusGallery: (dto.campusGallery ?? []).map((item) => ({
      label: safe(item.label),
      image: resolveAssetUrl(item.imageFull || item.image),
    })),
    preparations: (dto.preparations ?? []).map((item) => ({
      name: safe(item.name),
      desc: safe(item.desc),
    })),
    doctors: (dto.doctors ?? []).map((item) => ({
      name: safe(item.name),
      title: safe(item.title),
    })),
    advantageDiseases: (dto.advantageDiseases ?? []).map((item) => ({
      diseaseId: item.diseaseId,
      name: safe(item.name),
      desc: safe(item.desc),
    })),
  };
}

export function adaptHospitals(list: HospitalDto[] | undefined | null): HospitalItem[] {
  return (list ?? []).map(adaptHospital);
}

/** 病种 DTO -> 病种视图（列表与详情通用，详情额外含 detail/hospitalList） */
export function adaptDisease(dto: DiseaseDto): DiseaseItem {
  return {
    id: dto.id,
    name: safe(dto.name),
    image: resolveAssetUrl(dto.showImgFull || dto.showImg),
    symptom: safe(dto.symptom),
    detail: dto.detail ?? [],
    hospitalCount: dto.hospitalCount ?? 0,
    hospitalList: (dto.hospitalList ?? []).map((item) => ({
      hospitalId: item.hospitalId,
      name: safe(item.name),
      image: resolveAssetUrl(item.showImgFull || item.showImg),
      planNames: (item.planNames ?? []).map((name) => safe(name)).filter(Boolean),
    })),
  };
}

export function adaptDiseases(list: DiseaseDto[] | undefined | null): DiseaseItem[] {
  return (list ?? []).map(adaptDisease);
}

/** 成果转化 DTO -> 成果视图（列表与详情通用） */
export function adaptAchievement(dto: AchievementDto): AchievementItem {
  return {
    id: dto.id,
    title: safe(dto.title),
    date: safe(dto.publishDateStr || dto.publishDate),
    summary: safe(dto.summary),
    image: resolveAssetUrl(dto.showImgFull || dto.showImg),
    highlights: dto.highlights ?? [],
    detail: dto.detail ?? [],
  };
}

export function adaptAchievements(list: AchievementDto[] | undefined | null): AchievementItem[] {
  return (list ?? []).map(adaptAchievement);
}

/** 官网配置 DTO -> 官网配置视图（图片字段拼接资源域名） */
export function adaptPortalSetting(dto: PortalSettingDto): PortalSettingView {
  return {
    heroSlides: (dto.heroSlides ?? []).map((item) => ({
      id: item.id,
      tag: safe(item.tag),
      title: safe(item.title),
      subtitle: safe(item.subtitle),
      primaryText: safe(item.primaryText),
      primaryHref: safe(item.primaryHref),
      secondaryText: safe(item.secondaryText),
      secondaryHref: safe(item.secondaryHref),
      image: resolveAssetUrl(item.image),
    })),
    stats: (dto.stats ?? []).map((item) => ({
      id: item.id,
      icon: safe(item.icon),
      value: item.value ?? 0,
      suffix: safe(item.suffix),
      unit: safe(item.unit),
      label: safe(item.label),
    })),
    platformIntro: {
      title: safe(dto.platformIntro?.title),
      paragraphs: (dto.platformIntro?.paragraphs ?? []).map((item) => safe(item)).filter(Boolean),
      image: resolveAssetUrl(dto.platformIntro?.image),
      positionIcon: safe(dto.platformIntro?.positionIcon),
      positionLabel: safe(dto.platformIntro?.positionLabel),
      positionText: safe(dto.platformIntro?.positionText),
    },
    introHighlights: (dto.introHighlights ?? []).map((item) => ({
      id: item.id,
      icon: safe(item.icon),
      title: safe(item.title),
      desc: safe(item.desc),
    })),
    enrollNotices: (dto.enrollNotices ?? []).map((item) => safe(item)).filter(Boolean),
  };
}
